import React, { useEffect, useMemo, useState, useRef } from "react";
import * as d3 from "d3";
import { aggregateMarksWithoutAttributes } from "../../services/marketdata/marketDataUtilities";
import {
  isFiniteNumber,
  toFiniteNumber,
} from "../../services/utilities";
import ChartLegend from "./chartLegend";
import {
  getTimeAxisTicks,
  getLinearAxisTickCount,
} from "./d3ChartTickUtilities";
import {
  buildQualityAttributeNameLookup,
  formatReportedPriceTooltip,
} from "./tooltipFormatters";
import {
  appendCircleSeries,
  appendVerticalAreaGradient,
  buildPeriodAverageSeries,
  createTooltipHandlers,
  drawTimePriceAxes,
  drawTimePriceGrid,
  initializeChartCanvas,
  removeTooltip,
} from "./d3ChartShared";
import {
  getNormalizedDateRange,
  parseDateOrNull,
} from "../../utils/dateUtils";
import "./d3-charts.css";

const getDisplayPrice = (mark) => {
  const explicitPrice = toFiniteNumber(mark?.price);
  if (explicitPrice !== null) return explicitPrice;

  const bid = toFiniteNumber(mark?.bid);
  const offer = toFiniteNumber(mark?.offer);
  if (bid === null || offer === null) return null;

  return (bid + offer) / 2;
};

const CandlestickChart = ({
  data,
  historicalReportingPeriodicity,
  startDate,
  endDate,
  yMin,
  yMax,
  currencySymbol,
  pricePrecision,
  qualityAttributes,
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const qualityAttributeNameLookup = useMemo(
    () => buildQualityAttributeNameLookup(qualityAttributes),
    [qualityAttributes]
  );

  useEffect(() => {
    const chartContext = initializeChartCanvas({
      svgRef,
      tooltipRef,
      setDimensions,
    });
    if (!chartContext) return undefined;

    const { chartSvg, innerWidth, innerHeight, margin } = chartContext;

    const { normalizedStartDate, normalizedEndDate } = getNormalizedDateRange(
      startDate,
      endDate,
      new Date(),
    );
    const safeData = Array.isArray(data) ? data : [];

    const dateSequence = (() => {
      switch (historicalReportingPeriodicity) {
        case "Hour":
          return d3.timeHours(
            d3.timeDay.offset(normalizedStartDate, 0),
            d3.timeDay.offset(normalizedEndDate, 1)
          );
        default:
          return d3.timeDays(
            d3.timeDay.offset(normalizedStartDate, 0),
            d3.timeDay.offset(normalizedEndDate, 1)
          );
      }
    })();

    const aggregatedData = aggregateMarksWithoutAttributes(safeData)
      .map((item) => ({
        ...item,
        chartDate: parseDateOrNull(item.marketDate),
        price: toFiniteNumber(item.price),
        high: toFiniteNumber(item.high),
        low: toFiniteNumber(item.low),
        open: toFiniteNumber(item.open),
        close: toFiniteNumber(item.close),
      }))
      .filter((item) => item.chartDate)
      .sort((a, b) => a.chartDate - b.chartDate);

    const chartData = safeData
      .map((mark) => ({
        ...mark,
        chartDate: parseDateOrNull(mark.marketDate),
        price: toFiniteNumber(mark.price),
        bid: toFiniteNumber(mark.bid),
        offer: toFiniteNumber(mark.offer),
      }))
      .map((mark) => ({
        ...mark,
        displayPrice: getDisplayPrice(mark),
      }))
      .filter((mark) => mark.chartDate);

    const averageLineData = buildPeriodAverageSeries({
      rows: chartData.filter(
        (mark) => mark.displayPrice != null && mark.markState === "Approved"
      ),
      valueAccessor: (mark) => mark.displayPrice,
    });
    const hasSpotAverageLine = averageLineData.length > 1;

    const calculateTimeInterval = () => {
      if (dateSequence.length > 1) {
        return dateSequence[1] - dateSequence[0];
      }

      return historicalReportingPeriodicity === "Hour"
        ? 1000 * 60 * 60
        : 1000 * 60 * 60 * 24;
    };

    const timeInterval = calculateTimeInterval();
    const domainSource = dateSequence.length > 0 ? dateSequence : aggregatedData.map((d) => d.chartDate);

    if (domainSource.length === 0) return undefined;

    const domainExtent = d3.extent(domainSource);
    const paddingDuration = timeInterval / 2;
    const paddedDomain = [
      new Date(domainExtent[0].getTime() - paddingDuration),
      new Date(domainExtent[1].getTime() + paddingDuration),
    ];

    const xScale = d3.scaleTime().domain(paddedDomain).range([0, innerWidth]);

    const rawDataValues = [
      ...safeData.map((d) => toFiniteNumber(d.price)),
      ...safeData.map((d) => toFiniteNumber(d.bid)),
      ...safeData.map((d) => toFiniteNumber(d.offer)),
    ].filter((v) => v != null);

    const aggregatedDataValues = [
      ...aggregatedData.map((d) => d.high),
      ...aggregatedData.map((d) => d.low),
      ...aggregatedData.map((d) => d.open),
      ...aggregatedData.map((d) => d.close),
      ...aggregatedData.map((d) => d.price),
    ].filter((v) => v != null);

    const allValues = [...rawDataValues, ...aggregatedDataValues];
    const calculatedMin = allValues.length > 0 ? Math.min(...allValues) : 0;
    const calculatedMax = allValues.length > 0 ? Math.max(...allValues) : 0;

    const baseMinInput = toFiniteNumber(yMin);
    const baseMaxInput = toFiniteNumber(yMax);
    const rawBaseMin = baseMinInput ?? Math.min(0, calculatedMin);
    const rawBaseMax = baseMaxInput ?? calculatedMax;
    const baseMin = Math.min(rawBaseMin, rawBaseMax);
    const baseMax = Math.max(rawBaseMin, rawBaseMax);

    const yPadding = (baseMax - baseMin || 1) * 0.06;

    const yScale = d3
      .scaleLinear()
      .domain([baseMin - yPadding, baseMax + yPadding])
      .range([innerHeight, 0]);

    const periodDuration =
      historicalReportingPeriodicity === "Hour"
        ? 1000 * 60 * 60
        : 1000 * 60 * 60 * 24;

    const totalRange = Math.max(normalizedEndDate - normalizedStartDate, periodDuration);
    const xBandWidth =
      (periodDuration / totalRange) *
      Math.max(xScale(new Date(normalizedEndDate)) - xScale(new Date(normalizedStartDate)), 20);

    const tickFormat = (() => {
      switch (historicalReportingPeriodicity) {
        case "Hour":
          return "%a %H:%M";
        default:
          return "%b-%d";
      }
    })();

    const tickValues = getTimeAxisTicks(domainSource, innerWidth, 80);
    const maxYTicks = getLinearAxisTickCount(innerHeight, 40);

    drawTimePriceGrid({
      chartSvg,
      xScale,
      yScale,
      tickValues,
      maxYTicks,
      innerWidth,
      innerHeight,
    });

    const gradientId = appendVerticalAreaGradient({
      chartSvg,
      idPrefix: "d3-candle-area",
      color: "var(--theme-chart-accent-teal)",
      stops: [
        { offset: "0%", opacity: 0.48 },
        { offset: "46%", opacity: 0.2 },
        { offset: "100%", opacity: 0 },
      ],
    });

    const curveArea = d3
      .area()
      .defined((d) => d.price != null)
      .x((d) => xScale(d.chartDate))
      .y0(innerHeight)
      .y1((d) => yScale(d.price))
      .curve(d3.curveMonotoneX);

    const curveLine = d3
      .line()
      .defined((d) => d.price != null)
      .x((d) => xScale(d.chartDate))
      .y((d) => yScale(d.price))
      .curve(d3.curveMonotoneX);
    const areaData = averageLineData;

    if (areaData.length > 1) {
      chartSvg
        .append("path")
        .datum(areaData)
        .attr("class", "d3-chart-area d3-chart-area-secondary")
        .attr("fill", `url(#${gradientId})`)
        .attr("d", curveArea);
    }

    if (hasSpotAverageLine) {
      chartSvg
        .append("path")
        .datum(averageLineData)
        .attr("class", "d3-chart-line d3-chart-line-spot-average")
        .attr("d", curveLine);
    }

    chartSvg
      .selectAll(".high-low-line")
      .data(
        aggregatedData.filter(
          (d) => isFiniteNumber(d.high) && isFiniteNumber(d.low)
        )
      )
      .enter()
      .append("line")
      .attr("class", "d3-chart-high-low-line")
      .attr("x1", (d) => xScale(d.chartDate))
      .attr("x2", (d) => xScale(d.chartDate))
      .attr("y1", (d) => yScale(d.high))
      .attr("y2", (d) => yScale(d.low));

    chartSvg
      .selectAll(".candlestick")
      .data(
        aggregatedData.filter(
          (d) => isFiniteNumber(d.open) && isFiniteNumber(d.close)
        )
      )
      .enter()
      .append("rect")
      .attr(
        "class",
        (d) =>
          `d3-chart-candlestick ${d.open < d.close ? "bullish" : "bearish"}`
      )
      .attr("x", (d) => xScale(d.chartDate) - xBandWidth / 2)
      .attr("y", (d) => yScale(Math.max(d.open, d.close)))
      .attr("width", Math.max(xBandWidth, 2))
      .attr("height", (d) => Math.max(Math.abs(yScale(d.open) - yScale(d.close)), 1));


    const { showTooltip, hideTooltip, moveTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    if (hasSpotAverageLine) {
      const averageDateBisector = d3.bisector((d) => d.chartDate).center;

      const getNearestAveragePoint = (event) => {
        const [pointerX] = d3.pointer(event, chartSvg.node());
        const hoveredDate = xScale.invert(pointerX);
        const nearestIndex = averageDateBisector(averageLineData, hoveredDate);
        const clampedIndex = Math.max(
          0,
          Math.min(nearestIndex, averageLineData.length - 1)
        );

        return averageLineData[clampedIndex];
      };

      const showAverageTooltipAtPointer = (event) => {
        const nearestPoint = getNearestAveragePoint(event);
        if (!nearestPoint) return;

        showTooltip(
          event,
          `Average: <strong>${currencySymbol}${nearestPoint.price.toFixed(pricePrecision)}</strong>`
        );
      };

      chartSvg
        .append("path")
        .datum(averageLineData)
        .attr("class", "d3-chart-line-hover-target")
        .attr("d", curveLine)
        .on("mouseover", showAverageTooltipAtPointer)
        .on("mousemove", showAverageTooltipAtPointer)
        .on("mouseout", hideTooltip);
    }

    const approvedData = chartData.filter(
      (mark) => mark.displayPrice != null && mark.markState === "Approved"
    );
    const submittedData = chartData.filter(
      (mark) => mark.displayPrice != null && mark.markState === "Submitted"
    );
    const bidData = chartData.filter((mark) => mark.bid != null);
    const offerData = chartData.filter((mark) => mark.offer != null);
    const reportedPriceRadius = 2.5;

    appendCircleSeries({
      chartSvg,
      selector: ".price-circle",
      rows: approvedData,
      className: "d3-chart-circle d3-chart-circle-price-approved d3-chart-circle-spot-price",
      yAccessor: (d) => d.displayPrice,
      radius: reportedPriceRadius,
      tooltipAccessor: (d) =>
        formatReportedPriceTooltip(
          { ...d, price: d.displayPrice },
          currencySymbol,
          pricePrecision,
          qualityAttributeNameLookup
        ),
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      selector: ".price-circle",
      rows: submittedData,
      className: "d3-chart-circle d3-chart-circle-price-submitted",
      yAccessor: (d) => d.displayPrice,
      radius: 2.35,
      tooltipAccessor: (d) =>
        `<strong>${currencySymbol}${d.displayPrice.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      selector: ".bid-circle",
      rows: bidData,
      className: "d3-chart-circle d3-chart-circle-bid",
      yAccessor: (d) => d.bid,
      radius: 1.6,
      tooltipAccessor: (d) =>
        `Bid: <strong>${currencySymbol}${d.bid.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    appendCircleSeries({
      chartSvg,
      selector: ".offer-circle",
      rows: offerData,
      className: "d3-chart-circle d3-chart-circle-offer",
      yAccessor: (d) => d.offer,
      radius: 1.6,
      tooltipAccessor: (d) =>
        `Offer: <strong>${currencySymbol}${d.offer.toFixed(pricePrecision)}</strong>`,
      xScale,
      yScale,
      showTooltip,
      moveTooltip,
      hideTooltip,
    });

    drawTimePriceAxes({
      chartSvg,
      xScale,
      yScale,
      tickValues,
      tickFormat,
      maxYTicks,
      currencySymbol,
      pricePrecision,
      innerHeight,
    });

    chartSvg
      .append("text")
      .attr("class", "d3-chart-axis-label")
      .attr("x", 0 - innerHeight / 2)
      .attr("y", -margin.left + 8)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Price");

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [
    data,
    currencySymbol,
    endDate,
    historicalReportingPeriodicity,
    pricePrecision,
    startDate,
    yMax,
    yMin,
    qualityAttributeNameLookup,
  ]);

  return (
    <div className="d3-chart-container">
      <ChartLegend
        data={data}
        chartType="candlestick"
      />
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height}>
        {/* Chart will be drawn here */}
      </svg>
    </div>
  );
};

export default CandlestickChart;
